import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import cciplTemplateUrl from '../assets/CCIPL - template.xlsx';

/**
 * Exports data to an Excel file.
 * Can handle single or multiple sheets.
 * @param {Array<Object>} sheets - An array of sheet definition objects.
 *        Each object must have `name` (string) and `data` (Array<Array<any>> or Array<Object>) properties.
 *        Optionally, a `dataType` ('aoa' or 'json') property can be on each sheetDef to override default.
 * @param {string} baseFileName - The base name for the Excel file (e.g., "OrdersExport").
 *        A timestamp will be appended automatically.
 * @param {string} [defaultDataType='aoa'] - The default data type for sheet data if not specified per sheet
 *        by a `dataType` property on the sheet definition object. Valid values: 'aoa', 'json'.
 * @param {number} [itemCountForMessage] - Optional. The number of items (e.g., orders) actually being exported,
 *        used for a more specific success message.
 * @returns {boolean} - True if export was initiated successfully, false otherwise.
 */
export const exportDataToExcel = (sheets, baseFileName, defaultDataType = 'aoa', itemCountForMessage) => {
    if (!Array.isArray(sheets) || sheets.length === 0) {
        toast.info('No data provided for export.');
        return false;
    }

    try {
        const workbook = XLSX.utils.book_new();
        let sheetsAdded = 0;

        for (const sheetDef of sheets) {
            if (typeof sheetDef !== 'object' || sheetDef === null ||
                typeof sheetDef.name !== 'string' || !Array.isArray(sheetDef.data)) {
                console.warn('Skipping invalid sheet definition:', sheetDef);
                toast.warn(`Skipping sheet due to invalid format: ${sheetDef?.name || 'Unknown'}`);
                continue;
            }

            const currentDataType = sheetDef.dataType || defaultDataType;
            let worksheet;

            if (currentDataType === 'aoa') {
                // Ensure data is an array of arrays for aoa_to_sheet
                if (sheetDef.data.length === 0 || (sheetDef.data.length > 0 && Array.isArray(sheetDef.data[0]))) {
                    worksheet = XLSX.utils.aoa_to_sheet(sheetDef.data);
                } else {
                    console.error(`Data for sheet "${sheetDef.name}" is not an array of arrays as expected for 'aoa' type.`);
                    toast.error(`Invalid data format for sheet "${sheetDef.name}" (expected array of arrays).`);
                    continue;
                }
            } else if (currentDataType === 'json') {
                 // Ensure data is an array of objects for json_to_sheet
                if (sheetDef.data.length === 0 || (sheetDef.data.length > 0 && typeof sheetDef.data[0] === 'object' && sheetDef.data[0] !== null && !Array.isArray(sheetDef.data[0]))) {
                    worksheet = XLSX.utils.json_to_sheet(sheetDef.data);
                } else {
                    console.error(`Data for sheet "${sheetDef.name}" is not an array of objects as expected for 'json' type.`);
                    toast.error(`Invalid data format for sheet "${sheetDef.name}" (expected array of objects).`);
                    continue;
                }
            } else {
                toast.error(`Unsupported data type "${currentDataType}" for sheet "${sheetDef.name}".`);
                console.error(`Unsupported data type "${currentDataType}" for sheet "${sheetDef.name}".`);
                continue;
            }
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetDef.name);
            sheetsAdded++;
        }

        if (sheetsAdded === 0) {
            toast.error('No valid sheets could be added to the Excel file.');
            return false;
        }

        const fullFileName = `${baseFileName}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fullFileName);

        let itemsMessage = 'data';
        if (itemCountForMessage) {
            itemsMessage = `${itemCountForMessage} ${itemCountForMessage === 1 ? 'item' : 'items'}`;
            // Make message more specific if it's a single order export
            if (itemCountForMessage === 1 && baseFileName.toLowerCase().includes("order")) {
                itemsMessage = '1 order';
            }
        }
        toast.success(`Successfully exported ${itemsMessage} to ${fullFileName}!`);
        return true;

    } catch (error) {
        console.error('Error exporting to Excel:', error);
        toast.error(error.response?.data?.message || error.message || 'Failed to export data. Please try again.');
        return false;
    }
};

/**
 * Runtime loader for ExcelJS (browser build) from CDN to preserve template styling.
 * - Avoids npm install; attaches window.ExcelJS
 */
const loadExcelJSFromCDN = async () => {
    if (window.ExcelJS) return window.ExcelJS;
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js';
        script.async = true;
        script.onload = () => resolve(window.ExcelJS);
        script.onerror = () => reject(new Error('Failed to load ExcelJS from CDN.'));
        document.head.appendChild(script);
    });
};

/**
 * Shallow/deep clone basic cell style props from src -> dst to preserve look when adding rows.
 */
const cloneCellStyle = (srcCell, dstCell) => {
    if (!srcCell || !dstCell) return;
    // Primary style bag
    if (srcCell.style) dstCell.style = JSON.parse(JSON.stringify(srcCell.style));
    // Common explicit props
    if (srcCell.font) dstCell.font = JSON.parse(JSON.stringify(srcCell.font));
    if (srcCell.alignment) dstCell.alignment = JSON.parse(JSON.stringify(srcCell.alignment));
    if (srcCell.border) dstCell.border = JSON.parse(JSON.stringify(srcCell.border));
    if (srcCell.fill) dstCell.fill = JSON.parse(JSON.stringify(srcCell.fill));
    if (srcCell.numFmt) dstCell.numFmt = srcCell.numFmt;
};

/**
 * Export CCIPL Excel file from a single order using the provided CCIPL template.
 * - Loads the "CCIPL - template.xlsx" asset
 * - Fills the "Dedence" sheet with one row per product/status group
 *   (splits a product into multiple rows if its serials have different functional statuses)
 * - Leaves unknown fields empty
 * 
 * @param {Object} order - The order object (as used in OrderDetailsModal)
 * @param {Object} [productDetailsMap={}] - Optional. Map of productId -> full product document
 *        Used to fetch serialNumberNotes (for functionalStatus), militaryOrDualUse, etc.
 * @returns {Promise<boolean>} true on success, false on failure
 */
export const exportOrderToCCIPLExcel = async (order, productDetailsMap = {}) => {
    if (!order || !Array.isArray(order.products) || order.products.length === 0) {
        toast.info('No order or products to export.');
        return false;
    }

    const toKg = (weight, unit) => {
        if (typeof weight !== 'number') return 0;
        return unit === 'g' ? weight / 1000 : weight;
    };

    try {
        const ExcelJS = await loadExcelJSFromCDN();

        // 1) Load the template from assets (as blob/arrayBuffer)
        const response = await fetch(cciplTemplateUrl);
        if (!response.ok) throw new Error('Failed to fetch the CCIPL template file.');
        const arrayBuffer = await response.arrayBuffer();

        // 2) Open with ExcelJS (preserves styles/merges)
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        // 3) Select target sheet
        const dedence = workbook.worksheets.find(ws => ws.name === 'Dedence');
        const ws = dedence || workbook.worksheets[0];
        if (!ws) throw new Error('Template sheet not found.');

        // 3.0.5) Break all shared formulas in the template to prevent corruption when inserting rows
        ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
                // Check if cell has a shared formula
                if (cell.model && cell.model.sharedFormula) {
                    try {
                        // Get the actual formula value (this expands shared formulas)
                        const actualFormula = cell.formula;
                        // Clear the cell completely
                        cell.value = null;
                        // Set it back as a regular formula (not shared)
                        if (actualFormula && typeof actualFormula === 'string') {
                            cell.value = { formula: actualFormula };
                        }
                    } catch (err) {
                        // If we can't access the formula, just clear it
                        console.warn(`Could not break shared formula at row ${rowNumber}, col ${colNumber}:`, err.message);
                        cell.value = null;
                    }
                }
            });
        });
        // 3.1) Fill header blocks (Destination, dates, order name/number) by scanning label cells and writing into the next cell to the right
        const normalizeCellText = (val) => {
            if (val == null) return '';
            if (typeof val === 'string') return val.trim();
            if (typeof val === 'number') return String(val);
            if (typeof val === 'object') {
                if (val.richText && Array.isArray(val.richText)) {
                    return val.richText.map(rt => rt.text || '').join('').trim();
                }
                if (val.text) return String(val.text).trim();
            }
            try { return String(val).trim(); } catch (_) { return ''; }
        };

        const writeRightOfLabel = (labelRegexes, value) => {
            if (value == null) value = '';
            const SEARCH_ROW_LIMIT = 30; // header area is above the table
            for (let r = 1; r <= Math.min(ws.rowCount || 30, SEARCH_ROW_LIMIT); r++) {
                const row = ws.getRow(r);
                let wrote = false;
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    if (wrote) return;
                    const t = normalizeCellText(cell.value);
                    if (!t) return;
                    const isMatch = labelRegexes.some(rx => rx.test(t));
                    if (isMatch) {
                        // write to the first empty-looking cell to the right (up to +4)
                        for (let k = 1; k <= 4; k++) {
                            const target = ws.getCell(r, colNumber + k);
                            const existing = normalizeCellText(target.value);
                            if (!existing) {
                                target.value = value;
                                // attempt to clone style from the label cell (font/alignment) if target has none
                                if (!target.font && cell.font) target.font = JSON.parse(JSON.stringify(cell.font));
                                if (!target.alignment && cell.alignment) target.alignment = JSON.parse(JSON.stringify(cell.alignment));
                                wrote = true;
                                break;
                            }
                        }
                    }
                });
            }
        };

        const formatDate = (d) => {
            try {
                return d ? new Date(d).toLocaleDateString('he-IL') : '';
            } catch (_) {
                return '';
            }
        };

        // Try to populate common header labels found in the template
        writeRightOfLabel([/destination/i], order?.destination || '');
        writeRightOfLabel([/from\s*date/i, /from\s*dt/i, /date\s*from/i], formatDate(order?.fromDate));
        writeRightOfLabel([/until\s*date/i, /to\s*date/i, /date\s*to/i], formatDate(order?.untilDate));
        writeRightOfLabel([/order\s*name/i], order?.orderName || '');
        writeRightOfLabel([/order\s*(number|no\.?)/i], order?.orderNumber || '');

        // 4) Build CCIPL rows from order: one row per product/status group
        const builtRows = [];
        for (const p of order.products) {
            const prodMaster = (p?.product && productDetailsMap?.[p.product]) ? productDetailsMap[p.product] : null;
            const serialNotes = Array.isArray(prodMaster?.serialNumberNotes) ? prodMaster.serialNumberNotes : [];
            const serialToStatus = new Map(serialNotes.map(sn => [sn.value, sn.functionalStatus]));

            // Resolve unit weight and price with fallback to master product if missing on order line
            const fallbackWeight = (prodMaster && typeof prodMaster.weight === 'number') ? prodMaster.weight : p.weight;
            const fallbackWeightUnit = prodMaster?.weightUnit || p.weightUnit;
            const unitKg = toKg(fallbackWeight, fallbackWeightUnit);
            const unitPrice = (typeof p.price === 'number')
                ? p.price
                : (prodMaster && typeof prodMaster.price === 'number' ? prodMaster.price : null);

            if (Array.isArray(p.serialNumbers) && p.serialNumbers.length > 0) {
                const groups = new Map(); // status -> serials[]
                for (const s of p.serialNumbers) {
                    const st = serialToStatus.get(s) || ''; // unknown => ''
                    if (!groups.has(st)) groups.set(st, []);
                    groups.get(st).push(s);
                }
                for (const [st, serials] of groups) {
                    const qty = serials.length;
                    builtRows.push({
                        I: p.productName || '',
                        J: serials.join(' '),
                        K: st || '',
                        L: prodMaster?.militaryOrDualUse || '',
                        M: prodMaster?.hsCode || '',
                        N: 'Israel',
                        O: qty,
                        G: '',
                        H: unitKg ? +(unitKg * qty).toFixed(3) : '',
                        P: unitPrice != null ? unitPrice : '',
                        Q: unitPrice != null ? +(unitPrice * qty).toFixed(2) : ''
                    });
                }
            } else {
                const qty = p.quantity || 0;
                builtRows.push({
                    I: p.productName || '',
                    J: p.productCode || (prodMaster?.productCode || ''), // Fill Part Number if no serials
                    K: '',
                    L: prodMaster?.militaryOrDualUse || '',
                    M: prodMaster?.hsCode || '',
                    N: 'Israel',
                    O: qty,
                    G: '',
                    H: unitKg ? +(unitKg * qty).toFixed(3) : '',
                    P: unitPrice != null ? unitPrice : '',
                    Q: unitPrice != null ? +(unitPrice * qty).toFixed(2) : ''
                });
            }
        }

        // 5) Determine target columns dynamically from header row and write starting at row 20
        const startRow = 20;
        const prototypeRow = ws.getRow(startRow);

        const colLetterToNumber = (L) => {
            if (!L) return undefined;
            const up = String(L).trim().toUpperCase();
            let n = 0;
            for (let i = 0; i < up.length; i++) {
                const code = up.charCodeAt(i);
                if (code < 65 || code > 90) continue; // A-Z
                n = n * 26 + (code - 64);
            }
            return n || undefined;
        };

        // Scan header rows (around row 13-15) to map headers -> columns
        const findHeaderCols = () => {
            const maxHeaderScan = 30;
            let headerRowIdx = -1;
            let subHeaderRowIdx = -1;
            for (let r = 1; r <= Math.min(ws.rowCount || 30, maxHeaderScan); r++) {
                const row = ws.getRow(r);
                const texts = [];
                row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    const t = normalizeCellText(cell.value);
                    texts[colNumber] = t;
                });
                const hasDesc = texts.some(t => /Description of Goods/i.test(t || ''));
                const hasSerial = texts.some(t => /Engraved Serial Number\/Part Number/i.test(t || ''));
                if (hasDesc && hasSerial) {
                    headerRowIdx = r;
                    subHeaderRowIdx = r + 1;
                    break;
                }
            }
            const getColBy = (rx, rowIdx) => {
                if (rowIdx <= 0) return undefined;
                let found;
                ws.getRow(rowIdx).eachCell({ includeEmpty: true }, (cell, colNumber) => {
                    const t = normalizeCellText(cell.value);
                    if (!found && rx.test(t || '')) found = colNumber;
                });
                return found;
            };
            const cols = {
                I: getColBy(/Description of Goods/i, headerRowIdx),
                J: getColBy(/Engraved Serial Number\/Part Number/i, headerRowIdx),
                K: getColBy(/REAL FUNCTIONAL/i, headerRowIdx),
                L: getColBy(/MILITARY OR DUAL USE/i, headerRowIdx),
                M: getColBy(/HS Code/i, headerRowIdx),
                N: getColBy(/Country of Origin/i, headerRowIdx),
                O: getColBy(/^Qty$/i, headerRowIdx),
                G: getColBy(/Gross\s*Wt/i, headerRowIdx),
                H: getColBy(/Net\s*Wt/i, headerRowIdx) || getColBy(/Gross\s*Wt/i, headerRowIdx),
                P: getColBy(/Unit Value/i, subHeaderRowIdx),
                Q: getColBy(/Total Value/i, subHeaderRowIdx),
            };
            // Fallback to fixed letters when detection fails
            const fallback = {
                I: colLetterToNumber('I'),
                J: colLetterToNumber('J'),
                K: colLetterToNumber('K'),
                L: colLetterToNumber('L'),
                M: colLetterToNumber('M'),
                N: colLetterToNumber('N'),
                O: colLetterToNumber('O'),
                G: colLetterToNumber('G'),
                H: colLetterToNumber('H'),
                P: colLetterToNumber('P'),
                Q: colLetterToNumber('Q'),
            };
            for (const key of Object.keys(cols)) {
                if (typeof cols[key] !== 'number') cols[key] = fallback[key];
            }
            return cols;
        };

        const colNums = findHeaderCols();

        const setCellByCol = (colNumber, rowIdx, val) => {
            const c = ws.getCell(rowIdx, colNumber);
            if (val == null || val === '') {
                c.value = null;
            } else {
                c.value = typeof val === 'number' ? val : String(val);
            }
        };

        for (let i = 0; i < builtRows.length; i++) {
            const targetRowIdx = startRow + i;

            // Always insert a clone row for rows after the first, so we get row-20's look at 21, 22, 23, ...
            if (i > 0) {
                ws.insertRow(targetRowIdx, []);
            }

            // Clone styles for ALL columns from the prototype (row 20) into the target row
            const maxCol = ws.columnCount || 30;
            for (let col = 1; col <= maxCol; col++) {
                const src = ws.getCell(startRow, col);
                const dst = ws.getCell(targetRowIdx, col);
                cloneCellStyle(src, dst);
            }
            // Preserve row height if defined
            if (prototypeRow && prototypeRow.height) {
                ws.getRow(targetRowIdx).height = prototypeRow.height;
            }

            // Write values into detected columns
            const data = builtRows[i];
            setCellByCol(colNums.I, targetRowIdx, data.I);
            setCellByCol(colNums.J, targetRowIdx, data.J);
            setCellByCol(colNums.K, targetRowIdx, data.K);
            setCellByCol(colNums.L, targetRowIdx, data.L);
            setCellByCol(colNums.M, targetRowIdx, data.M);
            setCellByCol(colNums.N, targetRowIdx, data.N);
            setCellByCol(colNums.O, targetRowIdx, data.O);
            if (typeof colNums.G === 'number') setCellByCol(colNums.G, targetRowIdx, data.G);
            setCellByCol(colNums.H, targetRowIdx, data.H);
            setCellByCol(colNums.P, targetRowIdx, data.P);
            setCellByCol(colNums.Q, targetRowIdx, data.Q);
        }

        // 6) Save with original look preserved
        const baseName = `CCIPL_${order.orderName || order.orderNumber || 'Order'}`;
        const fullFileName = `${baseName}_${new Date().toISOString().split('T')[0]}.xlsx`;

        const buffer = await workbook.xlsx.writeBuffer();
        const blobOut = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blobOut);
        const a = document.createElement('a');
        a.href = url;
        a.download = fullFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success(`Successfully exported CCIPL file to ${fullFileName}!`);
        return true;
    } catch (err) {
        console.error('Error exporting CCIPL:', err);
        toast.error(err?.message || 'Failed to export CCIPL file (style-preserving path).');
        return false;
    }
};

// Alias name to match phrasing
export const exportCCIPLExcelFileFromOrders = exportOrderToCCIPLExcel;
